import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Rating,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Description as ResumeIcon,
  Event as EventIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { candidatesApi, locationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  CandidateWithInterviews,
  CandidateUpdate,
  CandidateInterviewCreate,
  CandidateStatus,
  CandidateSource,
  InterviewType,
  InterviewRecommendation,
} from '../types';

const statusLabels: Record<CandidateStatus, string> = {
  new: 'New',
  screening: 'Screening',
  interview_scheduled: 'Interview Scheduled',
  interview_completed: 'Interview Completed',
  offer_extended: 'Offer Extended',
  offer_accepted: 'Offer Accepted',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const sourceLabels: Record<CandidateSource, string> = {
  indeed: 'Indeed',
  linkedin: 'LinkedIn',
  referral: 'Referral',
  walk_in: 'Walk-in',
  company_website: 'Company Website',
  job_fair: 'Job Fair',
  other: 'Other',
};

const interviewTypeLabels: Record<InterviewType, string> = {
  phone_screen: 'Phone Screen',
  in_person: 'In Person',
  video: 'Video',
  panel: 'Panel',
  technical: 'Technical',
};

const recommendationLabels: Record<InterviewRecommendation, string> = {
  advance: 'Advance',
  hold: 'Hold',
  reject: 'Reject',
};

const recommendationColors: Record<InterviewRecommendation, 'success' | 'warning' | 'error'> = {
  advance: 'success',
  hold: 'warning',
  reject: 'error',
};

const formatTimeToAMPM = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const CandidateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isManager } = useAuth();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [error, setError] = useState('');

  const [editData, setEditData] = useState<CandidateUpdate>({});
  const [interviewData, setInterviewData] = useState<CandidateInterviewCreate>({
    interview_date: new Date().toLocaleDateString('en-CA'),
    interview_time: '',
    interview_type: 'phone_screen',
    location: '',
    notes: '',
    rating: undefined,
    recommendation: undefined,
  });

  // Query
  const { data: candidate, isLoading } = useQuery(
    ['candidate', id],
    () => candidatesApi.getCandidate(Number(id)),
    { enabled: !!id }
  );

  // Locations query
  const { data: locationsData } = useQuery(['locations'], () =>
    locationsApi.getLocations({ is_active: true }));
  const locationOptions = locationsData?.locations.map((l) => l.name) || [];

  // Mutations
  const updateMutation = useMutation(
    (data: CandidateUpdate) => candidatesApi.updateCandidate(Number(id), data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidate', id]);
        setEditDialogOpen(false);
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to update candidate');
      },
    }
  );

  const createInterviewMutation = useMutation(
    (data: CandidateInterviewCreate) => candidatesApi.createInterview(Number(id), data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidate', id]);
        setInterviewDialogOpen(false);
        setInterviewData({
          interview_date: new Date().toLocaleDateString('en-CA'),
          interview_time: '',
          interview_type: 'phone_screen',
          location: '',
          notes: '',
          rating: undefined,
          recommendation: undefined,
        });
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to create interview');
      },
    }
  );

  const deleteInterviewMutation = useMutation(candidatesApi.deleteInterview, {
    onSuccess: () => {
      queryClient.invalidateQueries(['candidate', id]);
    },
  });

  const uploadResumeMutation = useMutation(
    (file: File) => candidatesApi.uploadResume(Number(id), file),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidate', id]);
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to upload resume');
      },
    }
  );

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadResumeMutation.mutate(file);
    }
  };

  const handleDownloadResume = async () => {
    if (!candidate?.resume_filename) return;

    try {
      const blob = await candidatesApi.downloadResume(Number(id));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = candidate.resume_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to download resume');
    }
  };

  const openEditDialog = () => {
    if (candidate) {
      setEditData({
        first_name: candidate.first_name,
        last_name: candidate.last_name,
        email: candidate.email,
        phone: candidate.phone,
        position: candidate.position,
        location: candidate.location,
        source: candidate.source,
        status: candidate.status,
        notes: candidate.notes,
      });
      setEditDialogOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!candidate) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">Candidate not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/candidates')}
          sx={{ mb: 2 }}
        >
          Back to Candidates
        </Button>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {candidate.first_name} {candidate.last_name}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {candidate.position}
                {candidate.location && ` - ${candidate.location}`}
              </Typography>
              <Chip
                label={statusLabels[candidate.status]}
                color="primary"
                sx={{ mr: 1 }}
              />
              {candidate.source && (
                <Chip label={sourceLabels[candidate.source]} variant="outlined" sx={{ mr: 1 }} />
              )}
              {candidate.location && (
                <Chip label={candidate.location} variant="outlined" color="info" />
              )}
            </Box>
            {isManager && (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={openEditDialog}
                  sx={{ mr: 1 }}
                >
                  Edit
                </Button>
                <input
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="resume-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="resume-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                  >
                    {candidate.resume_filename ? 'Replace Resume' : 'Upload Resume'}
                  </Button>
                </label>
              </Box>
            )}
          </Box>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{candidate.email || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Phone</Typography>
              <Typography variant="body1">{candidate.phone || '-'}</Typography>
            </Grid>
            {candidate.resume_filename && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Resume</Typography>
                <Tooltip title="Click to download">
                  <Chip
                    icon={<ResumeIcon />}
                    label={candidate.resume_filename}
                    variant="outlined"
                    onClick={handleDownloadResume}
                    onDelete={handleDownloadResume}
                    deleteIcon={<DownloadIcon />}
                    sx={{ mt: 0.5, cursor: 'pointer' }}
                  />
                </Tooltip>
              </Grid>
            )}
            {candidate.notes && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Notes</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {candidate.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Interviews */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              Interviews ({candidate.interviews?.length || 0})
            </Typography>
            {isManager && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setInterviewDialogOpen(true)}
              >
                Add Interview
              </Button>
            )}
          </Box>

          {candidate.interviews && candidate.interviews.length > 0 ? (
            <List>
              {candidate.interviews.map((interview, index) => (
                <React.Fragment key={interview.id}>
                  {index > 0 && <Divider />}
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon fontSize="small" color="action" />
                          <Typography variant="subtitle1">
                            {new Date(interview.interview_date + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                            {interview.interview_time && ` at ${formatTimeToAMPM(interview.interview_time)}`}
                          </Typography>
                          <Chip
                            label={interviewTypeLabels[interview.interview_type]}
                            size="small"
                            variant="outlined"
                          />
                          {interview.recommendation && (
                            <Chip
                              label={recommendationLabels[interview.recommendation]}
                              size="small"
                              color={recommendationColors[interview.recommendation]}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {interview.location && (
                            <Typography variant="body2" color="text.secondary">
                              Location: {interview.location}
                            </Typography>
                          )}
                          {interview.interviewer && (
                            <Typography variant="body2" color="text.secondary">
                              Interviewer: {interview.interviewer.first_name} {interview.interviewer.last_name}
                            </Typography>
                          )}
                          {interview.rating && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                Rating:
                              </Typography>
                              <Rating value={interview.rating} readOnly size="small" />
                            </Box>
                          )}
                          {interview.notes && (
                            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                              {interview.notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {isManager && (
                      <ListItemSecondaryAction>
                        <Tooltip title="Delete Interview">
                          <IconButton
                            edge="end"
                            onClick={() => {
                              if (window.confirm('Delete this interview?')) {
                                deleteInterviewMutation.mutate(interview.id);
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No interviews recorded yet
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Candidate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editData.first_name || ''}
                onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editData.last_name || ''}
                onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Position"
                value={editData.position || ''}
                onChange={(e) => setEditData({ ...editData, position: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={editData.location || ''}
                  label="Location"
                  onChange={(e) => setEditData({ ...editData, location: e.target.value || undefined })}
                >
                  <MenuItem value="">None</MenuItem>
                  {(editData.location && !locationOptions.includes(editData.location)
                    ? [editData.location, ...locationOptions]
                    : locationOptions
                  ).map((loc) => (
                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editData.email || ''}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                value={editData.phone || ''}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editData.status || ''}
                  label="Status"
                  onChange={(e) => setEditData({ ...editData, status: e.target.value as CandidateStatus })}
                >
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={editData.source || ''}
                  label="Source"
                  onChange={(e) => setEditData({ ...editData, source: e.target.value as CandidateSource || undefined })}
                >
                  <MenuItem value="">None</MenuItem>
                  {Object.entries(sourceLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => updateMutation.mutate(editData)}
            disabled={updateMutation.isLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Interview Dialog */}
      <Dialog open={interviewDialogOpen} onClose={() => setInterviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Interview</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={interviewData.interview_date}
                onChange={(e) => setInterviewData({ ...interviewData, interview_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={interviewData.interview_time}
                onChange={(e) => setInterviewData({ ...interviewData, interview_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={interviewData.interview_type}
                  label="Type"
                  onChange={(e) => setInterviewData({ ...interviewData, interview_type: e.target.value as InterviewType })}
                >
                  {Object.entries(interviewTypeLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Location"
                value={interviewData.location}
                onChange={(e) => setInterviewData({ ...interviewData, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography component="legend">Rating</Typography>
              <Rating
                value={interviewData.rating || 0}
                onChange={(_, value) => setInterviewData({ ...interviewData, rating: value || undefined })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Recommendation</InputLabel>
                <Select
                  value={interviewData.recommendation || ''}
                  label="Recommendation"
                  onChange={(e) => setInterviewData({ ...interviewData, recommendation: e.target.value as InterviewRecommendation || undefined })}
                >
                  <MenuItem value="">None</MenuItem>
                  {Object.entries(recommendationLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={interviewData.notes}
                onChange={(e) => setInterviewData({ ...interviewData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterviewDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createInterviewMutation.mutate(interviewData)}
            disabled={createInterviewMutation.isLoading}
          >
            Add Interview
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CandidateDetail;
